import { useEffect, useState } from 'react';
import { EditableField } from './EditableField';
import { CustomerSearchModal } from './CustomerSearchModal';
import { SiteSearchModal } from './SiteSearchModal';
import { listSites, searchCustomers, listTodoke, listStaff, getWarehouseByCustomer, listWarehouses } from '../../../api/endpoints';
import { useCurrentStaff } from '../../../hooks/useCurrentStaff';
import { isCompleteDate } from '../state';
import type { OcrHeader, Site, Customer, Todoke, Staff } from '../../../types/api';

interface Props {
  header: OcrHeader;
  customerCode: string | null;
  siteCode: string | null;
  siteName: string | null;
  isEdited: (field: string) => boolean;
  onChange: (field: keyof OcrHeader, value: string) => void;
  onCustomerSelect: (code: string, name: string, staffCode?: string, nameEditable?: boolean, abbr?: string) => void;
  onSiteSelect: (code: string, name?: string, nameEditable?: boolean, abbr?: string) => void;
  onSiteNameChange?: (name: string) => void;
  customerNameEditable?: boolean;
  siteNameEditable?: boolean;
  suppressAutoSearch?: boolean;
  suppressSiteAutoMatch?: boolean;
  /** 見積(estimate)のとき true。先方要望で見積から外す項目
   * (時間指定/連絡先/受取人/届け先/相手伝票No./倉庫) を非表示にし、必須からも外す。 */
  isEstimate?: boolean;
  /** 伝票分割している（相手伝票No.は入力不可・ERPへ送らない） */
  slipSplit?: boolean;
}

/** 必須項目ラベル。ラベルに赤い必須マーク(*)を付け、未入力時は赤い「未入力」バッジを表示 */
function RequiredLabel({ children, missing }: { children: string; missing: boolean }) {
  return (
    <>
      <strong>
        {children}
        <span className="text-red-600" title="必須項目">*</span>:
      </strong>
      {missing && (
        <span className="text-[10px] leading-none px-1 py-0.5 rounded bg-red-100 text-red-700 font-medium">未入力</span>
      )}
    </>
  );
}

export function HeaderEditor({
  header,
  customerCode,
  siteCode,
  isEdited,
  onChange,
  onCustomerSelect,
  onSiteSelect,
  onSiteNameChange,
  siteName,
  customerNameEditable,
  siteNameEditable,
  suppressAutoSearch,
  suppressSiteAutoMatch,
  isEstimate,
  slipSplit,
}: Props) {
  const [modalOpen, setModalOpen] = useState(false);
  const [sites, setSites] = useState<Site[]>([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [siteModalOpen, setSiteModalOpen] = useState(false);
  const [todokeList, setTodokeList] = useState<Todoke[]>([]);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const { staffCode: myStaffCode, locked: staffLocked } = useCurrentStaff();

  // ログイン中はログインユーザーの担当者コードを入力担当者に固定する
  useEffect(() => {
    if (staffLocked && myStaffCode && header.input_staff_code !== myStaffCode) {
      onChange('input_staff_code', myStaffCode);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffLocked, myStaffCode, header.input_staff_code]);

  // 得意先候補
  const [customerCandidates, setCustomerCandidates] = useState<Customer[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  // 得意先名から候補を検索
  useEffect(() => {
    if (suppressAutoSearch) return;
    const name = header.customer_name;
    if (!name) {
      setCustomerCandidates([]);
      return;
    }
    let cancelled = false;
    setLoadingCustomers(true);
    void (async () => {
      try {
        const deptCode = staffList.find((s) => s.code === header.input_staff_code)?.department_code ?? '';
        const data = await searchCustomers(name, 1, 20, deptCode);
        if (!cancelled) {
          setCustomerCandidates(data.results);
          // 未選択 かつ 候補がある場合、1件目を自動選択
          if (!customerCode && data.results.length > 0) {
            const top = data.results[0];
            onCustomerSelect(top.code, top.name1 || top.abbr, top.staff_code, top.name_editable, top.abbr);
          }
        }
      } catch {
        // silent
      } finally {
        if (!cancelled) setLoadingCustomers(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [header.customer_name, suppressAutoSearch]);

  // 得意先コード確定時に現場一覧を取得 → OCR現場名で自動照合
  useEffect(() => {
    if (!customerCode) {
      setSites([]);
      return;
    }
    let cancelled = false;
    setLoadingSites(true);
    void (async () => {
      try {
        const data = await listSites({ tokcd: customerCode });
        if (cancelled) return;
        setSites(data.items);
        // OCR抽出の現場名で自動照合（現場未選択かつ自動マッチ抑制なしの場合のみ）
        if (!siteCode && siteName && !suppressSiteAutoMatch) {
          let bestMatch: typeof data.items[0] | null = null;
          let bestLen = 0;
          for (const s of data.items) {
            const name = s.name || '';
            const abbr = s.abbr || '';
            if (!name && !abbr) continue;
            const isMatch = (name && (name.includes(siteName!) || siteName!.includes(name))) ||
                            (abbr && (abbr.includes(siteName!) || siteName!.includes(abbr)));
            if (isMatch) {
              const matchLen = Math.max(name.length, abbr.length);
              if (matchLen > bestLen) {
                bestLen = matchLen;
                bestMatch = s;
              }
            }
          }
          if (bestMatch) {
            onSiteSelect(bestMatch.code, bestMatch.name || bestMatch.abbr, bestMatch.name_editable, bestMatch.abbr);
          }
        }
      } catch {
        // silent
      } finally {
        if (!cancelled) setLoadingSites(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerCode]);


  // 届け先マスタをロード
  useEffect(() => {
    void (async () => {
      try {
        const data = await listTodoke();
        setTodokeList(data.items);
        // OCRで読み取ったtodoke_typeから自動照合（選択不可の届け先は対象外）
        if (header.todoke_type && !header.todoke_code) {
          const match = data.items.find(
            (t) => t.selectable !== false
              && (t.name.includes(header.todoke_type!) || header.todoke_type!.includes(t.name)),
          );
          if (match) onChange('todoke_code', match.code);
        }
      } catch { /* silent */ }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 担当者マスタをロード
  useEffect(() => {
    void (async () => {
      try {
        const data = await listStaff();
        setStaffList(data.items);
      } catch { /* silent */ }
    })();
  }, []);

  // 倉庫マスタをロード
  const [warehouseList, setWarehouseList] = useState<Array<{ code: string; name: string }>>([]);
  useEffect(() => {
    void (async () => {
      try {
        const data = await listWarehouses();
        setWarehouseList(data.items.map((w) => ({ code: w['倉庫コード'] || '', name: w['倉庫名'] || '' })));
      } catch { /* silent */ }
    })();
  }, []);

  // 入力担当者名・部門を取得
  const matchedStaff = (header.input_staff_code ?? '').length === 4
    ? staffList.find((s) => s.code === header.input_staff_code!) ?? null
    : null;
  const staffLabel = matchedStaff
    ? `${matchedStaff.name}（${matchedStaff.department}）`
    : '';

  // 出荷担当者(SYUTANCD)。既定は空欄（入力担当者は自動セットしない）。
  const shippingStaffCode = header.shipping_staff_code || '';
  const matchedShippingStaff = shippingStaffCode.length === 4
    ? staffList.find((s) => s.code === shippingStaffCode) ?? null
    : null;
  const shippingStaffLabel = matchedShippingStaff
    ? `${matchedShippingStaff.name}（${matchedShippingStaff.department}）`
    : '';

  // 得意先コードから倉庫を自動取得
  const [, setWarehouseInfo] = useState<{ code: string; name: string }>({ code: '', name: '' });
  useEffect(() => {
    if (!customerCode) {
      setWarehouseInfo({ code: '', name: '' });
      onChange('warehouse_code', '');
      onChange('warehouse_name', '');
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const info = await getWarehouseByCustomer(customerCode);
        if (!cancelled) {
          setWarehouseInfo(info);
          onChange('warehouse_code', info.code);
          onChange('warehouse_name', info.name);
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerCode]);

  return (
    <div className="bg-slate-50 p-4 rounded mb-4 flex flex-wrap gap-x-6 gap-y-2 items-center">
      <div className="text-sm flex items-center gap-2">
        <RequiredLabel missing={!customerCode}>得意先</RequiredLabel>
        <button
          onClick={() => setModalOpen(true)}
          className="text-primary hover:underline text-left"
        >
          {loadingCustomers ? '検索中...' : customerCode
            ? `${customerCandidates.find((c) => c.code === customerCode)?.name1 || customerCandidates.find((c) => c.code === customerCode)?.abbr || header.customer_name || ''} (${customerCode})`
            : '（クリックして選択）'}
        </button>
        {customerNameEditable && customerCode && (
          <EditableField
            value={header.customer_name ?? ''}
            onChange={(v) => onChange('customer_name', v)}
            isEdited={isEdited('customer_name')}
            className="w-40"
          />
        )}
      </div>
      <div className="text-sm flex items-center gap-2">
        {isEstimate ? (
          <strong>現場:</strong>
        ) : (
          <RequiredLabel missing={!!customerCode && !siteCode}>現場</RequiredLabel>
        )}
        {customerCode ? (
          <>
            <button
              onClick={() => setSiteModalOpen(true)}
              className="text-primary hover:underline text-left"
            >
              {loadingSites ? '検索中...' : siteCode
                ? `${sites.find((s) => s.code === siteCode)?.name || sites.find((s) => s.code === siteCode)?.abbr || ''} (${siteCode})`
                : '（クリックして選択）'}
            </button>
            {siteCode && siteNameEditable && onSiteNameChange && (
              <EditableField
                value={siteName ?? ''}
                onChange={(v) => onSiteNameChange(v)}
                className="w-40"
              />
            )}
            {!siteCode && siteName && (
              <span className="text-xs text-amber-600">OCR: {siteName}</span>
            )}
          </>
        ) : (
          <span className="text-xs text-muted">得意先を先に確定</span>
        )}
      </div>
      {isEstimate && (
        <div className="text-sm flex items-center gap-2">
          <strong>件名:</strong>
          <EditableField
            value={header.project_name ?? ''}
            onChange={(v) => onChange('project_name', v)}
            isEdited={isEdited('project_name')}
            className="w-56"
          />
        </div>
      )}
      <div className="text-sm flex items-center gap-2">
        <RequiredLabel missing={!isCompleteDate(header.order_date)}>{isEstimate ? '見積日付' : '受注日付'}</RequiredLabel>
        {/* OCRで日付が読めない/不完全な場合でも手入力で補正できるよう編集可にする */}
        <input
          type="date"
          value={header.order_date ?? ''}
          onChange={(e) => onChange('order_date', e.target.value)}
          className={`border rounded px-1.5 py-0.5 text-xs font-mono ${
            isEdited('order_date') ? 'bg-amber-50 border-amber-300' : ''
          }`}
        />
      </div>
      <div className="text-sm flex items-center gap-2">
        <strong>注文者:</strong>
        <EditableField
          value={header.orderer_name ?? ''}
          onChange={(v) => onChange('orderer_name', v)}
          isEdited={isEdited('orderer_name')}
        />
      </div>
      {!isEstimate && (
        <>
          <div className="text-sm flex items-center gap-2">
            <strong>時間指定:</strong>
            <EditableField
              value={header.time_spec ?? ''}
              onChange={(v) => onChange('time_spec', v)}
              isEdited={isEdited('time_spec')}
            />
          </div>
          <div className="text-sm flex items-center gap-2">
            <strong>連絡先:</strong>
            <EditableField
              value={header.contact ?? ''}
              onChange={(v) => onChange('contact', v)}
              isEdited={isEdited('contact')}
              asciiOnly
              maxLength={15}
            />
          </div>
          <div className="text-sm flex items-center gap-2">
            <strong>受取人:</strong>
            <EditableField
              value={header.recipient ?? ''}
              onChange={(v) => onChange('recipient', v)}
              isEdited={isEdited('recipient')}
            />
          </div>
        </>
      )}
      <div className="text-sm flex items-center gap-2">
        <strong>備考:</strong>
        <EditableField
          value={header.note ?? ''}
          onChange={(v) => onChange('note', v)}
          isEdited={isEdited('note')}
        />
      </div>
      <div className="text-sm flex items-center gap-2">
        <RequiredLabel missing={!header.input_staff_code}>入力担当者</RequiredLabel>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={4}
          readOnly={staffLocked}
          title={staffLocked ? 'ログイン中のユーザーで自動設定されます' : undefined}
          style={{ width: '5.5ch' }}
          className={`border rounded px-1 py-0.5 text-xs font-mono text-center ${
            staffLocked ? 'bg-slate-100 text-muted cursor-not-allowed' : ''
          } ${isEdited('input_staff_code') ? 'bg-amber-50 border-amber-300' : ''}`}
          placeholder="0000"
          value={header.input_staff_code ?? ''}
          onChange={(e) => {
            if (staffLocked) return;
            const v = e.target.value.replace(/\D/g, '').slice(0, 4);
            onChange('input_staff_code', v);
          }}
          onBlur={() => {
            if (staffLocked) return;
            const v = header.input_staff_code ?? '';
            if (v && v.length < 4) onChange('input_staff_code', v.padStart(4, '0'));
          }}
        />
        {staffLabel && <span className="text-xs text-muted">{staffLabel}</span>}
      </div>
      {!isEstimate && (
        <div className="text-sm flex items-center gap-2">
          <label className="whitespace-nowrap">出荷担当者</label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={4}
            style={{ width: '5.5ch' }}
            className={`border rounded px-1 py-0.5 text-xs font-mono text-center ${
              isEdited('shipping_staff_code') ? 'bg-amber-50 border-amber-300' : ''
            }`}
            placeholder="0000"
            title="出荷担当者コード（任意）。未入力の場合は空欄で登録します"
            value={shippingStaffCode}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '').slice(0, 4);
              onChange('shipping_staff_code', v);
            }}
            onBlur={() => {
              const v = header.shipping_staff_code ?? '';
              if (v && v.length < 4) onChange('shipping_staff_code', v.padStart(4, '0'));
            }}
          />
          {shippingStaffLabel && <span className="text-xs text-muted">{shippingStaffLabel}</span>}
        </div>
      )}
      {!isEstimate && (
        <div className="text-sm flex items-center gap-2">
          <RequiredLabel missing={!header.todoke_code}>届け先</RequiredLabel>
          <select
            className={`border rounded px-1.5 py-0.5 text-xs font-mono ${
              isEdited('todoke_code') ? 'bg-amber-50 border-amber-300' : ''
            }`}
            value={header.todoke_code ?? ''}
            onChange={(e) => onChange('todoke_code', e.target.value)}
          >
            <option value="">-- 選択 --</option>
            {todokeList
              // selectable=false は選択肢に出さない（005 直送 は基幹システムでエラーになる）。
              // 一覧自体には残しておき、過去データの名称解決には使う。
              .filter((t) => t.selectable !== false || t.code === header.todoke_code)
              .map((t) => (
                <option key={t.code} value={t.code} disabled={t.selectable === false}>
                  {t.code} {t.name || '（自由入力）'}{t.selectable === false ? '（選択不可）' : ''}
                </option>
              ))}
          </select>
          {header.todoke_code === '088' && (
            <input
              type="text"
              className={`border rounded px-1.5 py-0.5 text-xs ${
                isEdited('todoke_name_manual') ? 'bg-amber-50 border-amber-300' : ''
              }`}
              placeholder="届け先を入力"
              value={header.todoke_name_manual ?? ''}
              onChange={(e) => onChange('todoke_name_manual', e.target.value)}
            />
          )}
        </div>
      )}

      {!isEstimate && (
        <div className="text-sm flex items-center gap-2">
          <strong>相手伝票No.:</strong>
          <EditableField
            value={slipSplit ? '' : (header.slip_no ?? '')}
            onChange={(v) => onChange('slip_no', v)}
            isEdited={isEdited('slip_no')}
            maxBytes={15}
            disabled={slipSplit}
          />
          {/* 分割した伝票に同じ相手伝票No.が並ぶと、ERPが2枚目以降を重複で拒否するため */}
          {slipSplit && (
            <span className="text-xs text-amber-700">伝票を分割しているため入力できません（ERPへは送りません）</span>
          )}
        </div>
      )}
      {/* 倉庫は見積でも表示する（受注と同じ。SOUCDは拠点判定に使うため） */}
      <div className="text-sm flex items-center gap-2">
        <strong>倉庫:</strong>
        <select
          className="border rounded px-1.5 py-0.5 text-xs"
          value={header.warehouse_code ?? ''}
          onChange={(e) => {
            const code = e.target.value;
            const wh = warehouseList.find((w) => w.code === code);
            onChange('warehouse_code', code);
            onChange('warehouse_name', wh?.name ?? '');
            setWarehouseInfo({ code, name: wh?.name ?? '' });
          }}
        >
          <option value="">-- 選択 --</option>
          {warehouseList.map((w) => (
            <option key={w.code} value={w.code}>{w.name}</option>
          ))}
        </select>
      </div>

      <CustomerSearchModal
        open={modalOpen}
        initialQuery={header.customer_name}
        departmentCode={matchedStaff?.department_code}
        onClose={() => setModalOpen(false)}
        onSelect={(code, name, staffCode, nameEditable, abbr) => {
          onCustomerSelect(code, name, staffCode, nameEditable, abbr);
          setModalOpen(false);
        }}
      />
      {customerCode && (
        <SiteSearchModal
          open={siteModalOpen}
          customerCode={customerCode}
          initialQuery=""
          onClose={() => setSiteModalOpen(false)}
          onSelect={(code, name, editable, abbr) => {
            onSiteSelect(code, name, editable, abbr);
            setSiteModalOpen(false);
          }}
        />
      )}
    </div>
  );
}
